import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api'
import { RefreshCw } from 'lucide-react'

type RunSummary = {
  id: number
  fund_id: string | null
  run_type: string | null
  model_name: string | null
  universe: string | null
  time_window: string | null
  as_of: string | null
  status: string | null
  started_at: string | null
  finished_at: string | null
  runtime_ms: number | null
  n_obs: number | null
  error: string | null
  git_sha: string | null
  config_hash: string | null
}

type VarRow = {
  model_name: string; horizon: number; confidence: number;
  var_value: number | null; es_value: number | null; n_samples: number | null; method: string | null
}
type StressRow = {
  scenario_name: string; model_name: string | null; mean: number | null;
  var_95: number | null; es_95: number | null; status: string | null; method: string | null
}
type FactorRow = {
  factor: string; beta: number | null; beta_ortho: number | null;
  t_stat: number | null; p_value: number | null; significant: boolean | null
}
type WeightRow = { model_name: string; weight: number }

type RunDetail = RunSummary & {
  var_results: VarRow[]
  stress_results: StressRow[]
  factor_results: FactorRow[]
  ensemble_weights: WeightRow[]
}

export default function RiskRuns() {
  const [filterType, setFilterType] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [selectedId, setSelectedId] = useState<number | null>(null)

  const q = useQuery<RunSummary[]>({
    queryKey: ['risk-runs', filterType, filterStatus],
    queryFn: () => {
      const params = new URLSearchParams()
      if (filterType) params.set('run_type', filterType)
      if (filterStatus) params.set('status', filterStatus)
      params.set('limit', '100')
      return api.get(`/risk-stats/runs?${params}`).then(r => r.data)
    },
    refetchInterval: 10_000,
  })

  const detail = useQuery<RunDetail>({
    queryKey: ['risk-runs', 'detail', selectedId],
    queryFn: () => api.get(`/risk-stats/runs/${selectedId}`).then(r => r.data),
    enabled: !!selectedId,
  })

  const pct = (v: number | null | undefined, d = 2) => v == null ? '—' : `${(v * 100).toFixed(d)}%`
  const num = (v: number | null | undefined, d = 3) => v == null ? '—' : v.toFixed(d)
  const fmtDate = (s: string | null | undefined) => s ? new Date(s).toLocaleString() : '—'
  const fmtMs = (v: number | null | undefined) => v == null ? '—' : v < 1000 ? `${v}ms` : `${(v / 1000).toFixed(1)}s`

  const statusPill = (s: string | null | undefined) => {
    if (s === 'completed') return 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30'
    if (s === 'running')   return 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30'
    if (s === 'failed')    return 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30'
    return 'bg-muted border-border text-muted-foreground'
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">Risk run registry</h1>
          <p className="text-muted-foreground">
            Every call to <span className="font-mono">/risk-stats/*</span> is persisted with git sha, config hash, runtime, and output. Auto-refreshes every 10s.
          </p>
        </div>
        <button
          type="button"
          onClick={() => q.refetch()}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-border text-sm hover:bg-muted"
        >
          <RefreshCw className={'h-4 w-4 ' + (q.isFetching ? 'animate-spin' : '')} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-3 items-center text-sm">
        <label className="flex items-center gap-2">
          <span className="text-muted-foreground">Type</span>
          <select value={filterType} onChange={e => setFilterType(e.target.value)} className="input">
            <option value="">all</option>
            <option value="var">var</option>
            <option value="stress">stress</option>
            <option value="factor">factor</option>
            <option value="ensemble">ensemble</option>
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-muted-foreground">Status</span>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="input">
            <option value="">all</option>
            <option value="running">running</option>
            <option value="completed">completed</option>
            <option value="failed">failed</option>
          </select>
        </label>
        <div className="text-muted-foreground">{q.data?.length ?? 0} runs</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* List */}
        <div className="lg:col-span-2 bg-card border border-border rounded-lg">
          <div className="max-h-[70vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card border-b border-border">
                <tr>
                  <th className="text-left py-2 px-3">id</th>
                  <th className="text-left py-2 px-3">type</th>
                  <th className="text-left py-2 px-3">fund</th>
                  <th className="text-left py-2 px-3">status</th>
                  <th className="text-right py-2 px-3">runtime</th>
                </tr>
              </thead>
              <tbody>
                {q.data?.map(r => (
                  <tr
                    key={r.id}
                    onClick={() => setSelectedId(r.id)}
                    className={'border-b border-border/50 cursor-pointer hover:bg-muted ' + (selectedId === r.id ? 'bg-primary/5' : '')}
                  >
                    <td className="py-2 px-3 font-mono text-xs">{r.id}</td>
                    <td className="py-2 px-3 text-xs">
                      <div>{r.run_type}</div>
                      <div className="text-muted-foreground">{r.model_name ?? '(multi)'}</div>
                    </td>
                    <td className="py-2 px-3 font-mono text-xs">{r.fund_id}</td>
                    <td className="py-2 px-3">
                      <span className={'text-xs px-2 py-0.5 rounded border ' + statusPill(r.status)}>
                        {r.status ?? '—'}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-xs">{fmtMs(r.runtime_ms)}</td>
                  </tr>
                ))}
                {(q.data?.length ?? 0) === 0 && (
                  <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">No runs.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail */}
        <div className="lg:col-span-3">
          {!selectedId && (
            <div className="bg-card border border-border rounded-lg p-6 text-center text-muted-foreground">
              Select a run to view details.
            </div>
          )}
          {selectedId && detail.isLoading && (
            <div className="bg-card border border-border rounded-lg p-6"><div className="spinner" /></div>
          )}
          {selectedId && detail.data && (
            <div className="space-y-4">
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs text-muted-foreground uppercase">Run</div>
                    <div className="font-mono">{detail.data.id} · {detail.data.run_type} · {detail.data.model_name ?? '(multi)'}</div>
                  </div>
                  <span className={'text-xs px-2 py-0.5 rounded border self-start ' + statusPill(detail.data.status)}>
                    {detail.data.status}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mt-4">
                  <KV k="fund" v={detail.data.fund_id} />
                  <KV k="universe" v={detail.data.universe} />
                  <KV k="window" v={detail.data.time_window} />
                  <KV k="as_of" v={detail.data.as_of} />
                  <KV k="n_obs" v={String(detail.data.n_obs ?? '')} />
                  <KV k="runtime" v={fmtMs(detail.data.runtime_ms)} />
                  <KV k="git sha" v={detail.data.git_sha} />
                  <KV k="config hash" v={detail.data.config_hash} />
                  <KV k="started" v={fmtDate(detail.data.started_at)} />
                  <KV k="finished" v={fmtDate(detail.data.finished_at)} />
                </div>
                {detail.data.error && (
                  <div className="mt-3 text-sm text-destructive whitespace-pre-wrap font-mono">
                    {detail.data.error}
                  </div>
                )}
              </div>

              {detail.data.var_results.length > 0 && (
                <DetailTable title="VaR results" cols={['model', 'horizon', 'conf', 'VaR', 'ES', 'n', 'method']}>
                  {detail.data.var_results.map((r, i) => (
                    <tr key={i} className="border-b border-border/50">
                      <td className="py-2 px-3 font-mono text-xs">{r.model_name}</td>
                      <td className="py-2 px-3 font-mono text-xs">{r.horizon}d</td>
                      <td className="py-2 px-3 font-mono text-xs">{Math.round(r.confidence * 100)}%</td>
                      <td className="py-2 px-3 font-mono text-xs text-right">{pct(r.var_value)}</td>
                      <td className="py-2 px-3 font-mono text-xs text-right">{pct(r.es_value)}</td>
                      <td className="py-2 px-3 font-mono text-xs text-right">{r.n_samples ?? '—'}</td>
                      <td className="py-2 px-3 font-mono text-xs">{r.method ?? '—'}</td>
                    </tr>
                  ))}
                </DetailTable>
              )}

              {detail.data.stress_results.length > 0 && (
                <DetailTable title="Stress results" cols={['scenario', 'model', 'mean', 'VaR 95', 'ES 95', 'status']}>
                  {detail.data.stress_results.map((r, i) => (
                    <tr key={i} className="border-b border-border/50">
                      <td className="py-2 px-3 text-xs">{r.scenario_name}</td>
                      <td className="py-2 px-3 font-mono text-xs">{r.model_name ?? '—'}</td>
                      <td className={'py-2 px-3 font-mono text-xs text-right ' + ((r.mean ?? 0) < 0 ? 'text-red-600' : 'text-green-600')}>{pct(r.mean)}</td>
                      <td className="py-2 px-3 font-mono text-xs text-right">{pct(r.var_95)}</td>
                      <td className="py-2 px-3 font-mono text-xs text-right">{pct(r.es_95)}</td>
                      <td className="py-2 px-3 text-xs">{r.status ?? '—'}</td>
                    </tr>
                  ))}
                </DetailTable>
              )}

              {detail.data.factor_results.length > 0 && (
                <DetailTable title="Factor exposures" cols={['factor', 'beta', 'beta ortho', 't-stat', 'p-value']}>
                  {detail.data.factor_results.map((r, i) => (
                    <tr key={i} className={'border-b border-border/50 ' + (r.significant ? 'bg-primary/5' : '')}>
                      <td className="py-2 px-3 font-mono text-xs">{r.factor}</td>
                      <td className="py-2 px-3 font-mono text-xs text-right">{num(r.beta)}</td>
                      <td className="py-2 px-3 font-mono text-xs text-right">{num(r.beta_ortho)}</td>
                      <td className="py-2 px-3 font-mono text-xs text-right">{num(r.t_stat, 2)}</td>
                      <td className="py-2 px-3 font-mono text-xs text-right">{num(r.p_value, 4)}</td>
                    </tr>
                  ))}
                </DetailTable>
              )}

              {detail.data.ensemble_weights.length > 0 && (
                <DetailTable title="Ensemble weights" cols={['engine', 'weight']}>
                  {detail.data.ensemble_weights.map((r, i) => (
                    <tr key={i} className="border-b border-border/50">
                      <td className="py-2 px-3 font-mono text-xs">{r.model_name}</td>
                      <td className="py-2 px-3 font-mono text-xs text-right">{num(r.weight, 3)}</td>
                    </tr>
                  ))}
                </DetailTable>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function KV({ k, v }: { k: string; v: string | number | null | undefined }) {
  return (
    <div>
      <div className="text-muted-foreground uppercase tracking-wider">{k}</div>
      <div className="font-mono break-all">{v ?? '—'}</div>
    </div>
  )
}

function DetailTable({ title, cols, children }: { title: string; cols: string[]; children: React.ReactNode }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <h3 className="font-medium mb-3">{title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              {cols.map(c => (
                <th key={c} className="text-left py-2 px-3 text-xs uppercase text-muted-foreground">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  )
}
